import { AppSettings } from "@/AppSettings";
import { SourceDataEventType } from "@/api/dtos";
import SourceDataForm from "@/components/forms/forms/source-data/SourceDataForm";
import ExcludedAlert from "@/components/layouts/ExcludedAlert";
import ExcludedEventAlert from "@/components/layouts/ExcludedEventAlert";
import ExcludedFormAlert from "@/components/layouts/ExcludedFormAlert";
import ExcludedParticipantAlert from "@/components/layouts/ExcludedParticipantAlert";
import { DefaultAlert } from "@/components/layouts/Messages";
import { getFormCompleteColor } from "@/components/lists/status/StatusListStats";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import ParticipantSourceDataFormStatusesContext from "@/contexts/data/ParticipantSourceDataFormStatusesContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import { ItemType } from "@/hooks/form/configuration/useItemConfigurations";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import useItemsVirtualized from "@/hooks/form/source-data/useItemsVirtualized";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Chip, styled, Tooltip, TooltipProps, useTheme } from "@mui/material";
import { FC, useContext, useDeferredValue, useMemo } from "react";
import { useParams } from "react-router-dom";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginBottom: theme.spacing(2), 
    fontSize: '1.5rem',
    gap: theme.spacing(2),
    //justifyContent: 'space-between',
    alignItems: 'center'
}))

const ChipBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginLeft: 'auto',
    gap: theme.spacing(2),

    '& > div': {
        paddingLeft: theme.spacing(0.5),
    },
    '& svg': {
        fontSize: '1.5rem'
    }
}))

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    marginTop: '12px !important'
}));

const FormStatus: FC<{
    showReviewedChip?: boolean;
    showVerifiedChip?: boolean;
}> = ({
    showReviewedChip,
    showVerifiedChip
}) => {
    const { studyEventOid, studyEventRepeat, caseReportFormOid } = useParams();

    const { idMap: formStatuses } = useDeferredValue(useContext(ParticipantSourceDataFormStatusesContext));

    const theme = useTheme();
    const formStatus = formStatuses?.[studyEventOid!]?.[+studyEventRepeat!]?.[caseReportFormOid!];

    // if (!formStatus?.verificationTotalCount && !formStatus?.reviewTotalCount) {
    //     return null;
    // }

    if(!showReviewedChip && !showVerifiedChip) {
        return null;
    }
    
    const verificationComplete = Math.floor((((formStatus?.verificationCompleteCount ?? 0) + (formStatus?.verificationNotApplicableCount ?? 0)) / (formStatus?.verificationTotalCount ?? 0)) * 100);

    const reviewComplete =  Math.floor((((formStatus?.reviewCompleteCount ?? 0) + (formStatus?.reviewNotApplicableCount ?? 0)) / (formStatus?.reviewTotalCount ?? 0)) * 100);

    return (
        <ChipBox>
            {/* <Chip 
                icon={<FontAwesomeIcon size="lg" icon={AppSettings.icons.review} />} 
                label={
                    <>
                        {!formStatus?.reviewTotalCount ? 100 : ((formStatus?.reviewCompleteCount ?? 0) / (formStatus?.reviewTotalCount ?? 0)) * 100}%
                    </>
                } 
            /> */}
            {
                showReviewedChip &&
                <StyledTooltip
                    followCursor
                    title={`${reviewComplete}% reviewed`}
                >
                    <Chip 
                        sx={{backgroundColor: getFormCompleteColor(theme, reviewComplete)}}
                        icon={<FontAwesomeIcon size="lg" icon={AppSettings.icons.review} />} 
                        label={
                            <>
                                {reviewComplete}%
                            </>
                        } 
                    />
                </StyledTooltip>
            }
            {
                showVerifiedChip &&
                <StyledTooltip
                    followCursor

                    title={`${verificationComplete}% verified`}
                >
                    <Chip 
                        sx={{backgroundColor: getFormCompleteColor(theme, verificationComplete)}}
                        icon={<FontAwesomeIcon size="lg" icon={AppSettings.icons.verification} />} 
                        label={
                            <>
                                {verificationComplete}%
                            </>
                        } 
                    />
                </StyledTooltip>
            }
        </ChipBox>
    )
}

const ParticipantSourceDataForm: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const { studyEventOid, studyEventRepeat, caseReportFormOid } = useParams();

    const event = useEvent(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        false,
        true
    )

    const form = useForm(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid,
        false,
        true
    )

    if (!trial) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Trial</AlertTitle>
                No trial was found.
            </Alert>
        )
    }

    if (!participant) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Participant</AlertTitle>
                No participant was found.
            </Alert>
        )
    }

    if (!metadata) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Metadata Could Not Be Retrieved</AlertTitle>
                The metadata for the {trial.name} trial could not be retrieved from OpenClinica.
            </Alert>
        )
    }

    if (!trialConfiguration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Configuration Could Not Be Retrieved</AlertTitle>
                The global configuration for the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!configuration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Allocated Configuration Could Not Be Retrieved</AlertTitle>
                The allocated configuration for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!sourceData) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Source Data Could Not Be Retrieved</AlertTitle>
                The source data for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!form?.formVersion?.caseReportFormVersionOid) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Case Report Form Version Could Not Be Retrieved</AlertTitle>
                The case report form version could not be retrieved.
            </Alert>
        )
    }

    const itemData = useItemsVirtualized(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid,
        form.formVersion.caseReportFormVersionOid
    );


    const eventConfiguration = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid
    );

    const formConfiguration = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid
    );

    const formVersionConfiguration = useFormVersionConfiguration(
        metadata,
        trialConfiguration,
        configuration,
        trial,
        configuration?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        form.formVersion.caseReportFormVersionOid
    );

    const allowed = useMemo(() => {
        const configurationAllowReview = eventConfiguration?.configuration?.allowReview === true && 
            formConfiguration?.configuration?.allowReview === true &&
            formVersionConfiguration?.configuration?.allowReview === true;

        const configurationAllowVerification = eventConfiguration?.configuration?.allowVerification === true && 
            formConfiguration?.configuration?.allowVerification === true &&
            formVersionConfiguration?.configuration?.allowVerification === true;

        const configurationAllowNotApplicable = eventConfiguration?.configuration?.allowNotApplicable === true && 
            formConfiguration?.configuration?.allowNotApplicable === true &&
            formVersionConfiguration?.configuration?.allowNotApplicable === true;

        return {
            configurationAllowReview,
            configurationAllowVerification,
            configurationAllowNotApplicable
        }
    }, [eventConfiguration, formConfiguration, formVersionConfiguration]);
    
    const allowReview = useMemo(() => {
        if(!allowed.configurationAllowReview) {
            return false;
        }

        if(!itemData) {
            return false;
        }

        for(let row of itemData) {
            if(row.type === ItemType.ItemGroup) {
                continue;
            }

            if((!row.itemGroup || row.itemGroup.configuration?.allowReview) && row.item.configuration?.allowReview) {
                return true;
            }
        }

        return false;
    }, [allowed.configurationAllowReview, itemData]);

    const allowVerification = useMemo(() => {
        if(!allowed.configurationAllowVerification) {
            return false;
        }

        if(!itemData) {
            return false;
        }

        for(let row of itemData) {
            if(row.type === ItemType.ItemGroup) {
                continue;
            }

            if((!row.itemGroup || row.itemGroup.configuration?.allowVerification) && row.item.configuration?.allowVerification) {
                return true;
            }
        }

        return false;
    }, [allowed.configurationAllowVerification, itemData]);
        
    return (
        <>
            <TitleBox>
                <FontAwesomeIcon icon={AppSettings.icons.form} /> {event?.displayName}{event?.eventMetadata.type === SourceDataEventType.Repeating && <> &mdash; {event.studyEventRepeat}</>}: {form?.displayName} ({form?.formVersion.displayName})
                {
                    !form.excluded && (
                        <FormStatus 
                            showReviewedChip={allowReview}
                            showVerifiedChip={allowVerification}
                        />
                    )
                }
            </TitleBox>
            <ExcludedAlert
                participant={participant}
                event={event}
                form={form}
            >
                <SourceDataForm
                    trial={trial}
                    masterGroup={masterGroup}
                    coordinatingGroup={coordinatingGroup}
                    institution={institution}
                    participant={participant}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                    studyEventOid={studyEventOid!}
                    studyEventRepeat={+studyEventRepeat!}
                    caseReportFormOid={caseReportFormOid!}
                    caseReportFormVersionOid={form.formVersion.caseReportFormVersionOid}
                    form={form}
                    event={event}
                />
            </ExcludedAlert>
        </>
    )
};

export default ParticipantSourceDataForm;